import React, { useMemo } from 'react';
import { ListOrdered, List } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import ViewToggle from '../ViewToggle';
import MatchListView from '../lists/MatchListView';
import { applyTieFormatToRounds } from '../../generators/tieFormats';

const RankRow = ({ entry, theme }) => (
  <div className={`flex items-center gap-3 rounded-xl border ${theme.border} p-3`}>
    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${theme.backgroundSecondary} ${theme.textMuted}`}>
      {entry.rank}
    </span>
    <span className={`text-sm font-semibold ${theme.text}`}>{entry.name}</span>
  </div>
);

const CourtCard = ({ court, theme }) => (
  <div className={`rounded-2xl border ${theme.border} ${theme.card} p-4`}>
    <div className={`mb-3 text-sm font-bold ${theme.text}`}>Court {court.court}</div>
    <div className="space-y-2">
      {court.participants.map((entry) => (
        <RankRow key={entry.id} entry={entry} theme={theme} />
      ))}
    </div>
  </div>
);

/**
 * Ladder result view — Round 1's real starting groups (the "Ladder" tab) and
 * every generated round's court-by-court round robin (the "Matches" tab).
 * Round 1 uses real participants; Round 2 onward uses PLACEHOLDER
 * participants ("Court 1 · 1st", ...) since who actually lands in each court
 * depends on results this schedule-preview tool can't simulate — same
 * placeholder convention group_knockout's knockout stage already uses before
 * pool play concludes (see ladderGenerator.js's own doc comment).
 */
const LadderView = ({
  schedule,
  viewMode,
  onViewModeChange,
  tieFormat,
  tieOverrides,
  matchOrderOverrides,
  reorderRow,
  sportType,
  tournamentMatchSettings,
  roundOverrides,
  rowOverrides,
  applyRoundOverride,
  applyRowOverride,
  filters,
  currentUserId,
  onClearFilters,
  tournamentVO,
  startScoring,
  openLiveGame,
  canManageThisSchedule,
  updateMatchSchedule,
  deleteMatchRow,
  assignMatchParticipants,
}) => {
  const { theme } = useTheme();
  const displayedLadderRounds = useMemo(
    () =>
      schedule.ladderRounds.map((ladderRound) => ({
        ...ladderRound,
        courts: ladderRound.courts.map((court) => ({ ...court, rounds: applyTieFormatToRounds(court.rounds, tieFormat, tieOverrides) })),
      })),
    [schedule.ladderRounds, tieFormat, tieOverrides]
  );
  const firstRoundCourts = schedule.ladderRounds[0].courts;

  return (
    <div>
      <div className="mb-4">
        <ViewToggle
          value={viewMode}
          onChange={onViewModeChange}
          options={[
            { value: 'ladder', label: 'Ladder', icon: ListOrdered },
            { value: 'matches', label: 'Matches', icon: List },
          ]}
        />
      </div>
      <p className={`mb-4 text-sm ${theme.textSecondary}`}>
        {schedule.numCourts} court{schedule.numCourts > 1 ? 's' : ''} of 4, seeded by entry order, over{' '}
        {schedule.totalLadderRounds} ladder round{schedule.totalLadderRounds > 1 ? 's' : ''}. After each court's round
        robin, the top 2 move up a court and the bottom 2 move down (Court 1 is the top tier) — Round 2 onward shows
        placeholder qualifiers until real results replace them.
      </p>
      {viewMode === 'matches' ? (
        <div className="space-y-8">
          {displayedLadderRounds.map((ladderRound) => (
            <div key={ladderRound.ladderRound}>
              <div className={`mb-3 text-sm font-bold ${theme.text}`}>Ladder Round {ladderRound.ladderRound}</div>
              <div className="space-y-6">
                {ladderRound.courts.map((court) => {
                  const listKey = `ladder:${ladderRound.ladderRound}:court:${court.court}`;
                  return (
                    <MatchListView
                      key={court.court}
                      rounds={court.rounds}
                      title={`Court ${court.court}`}
                      orderOverrides={matchOrderOverrides?.[listKey]}
                      onReorderRow={(roundNumber, order) => reorderRow(listKey, roundNumber, order)}
                      // No `courts` here deliberately — Ladder already has its
                      // own "Court N" concept (a seeding tier, see
                      // ladderGenerator.js), already shown as this list's own
                      // title. Showing a physical-court column/picker too
                      // would put two different "Court" meanings on screen at
                      // once. Custom match rules + Team Pairing still work.
                      sportType={sportType}
                      tournamentMatchSettings={tournamentMatchSettings}
                      roundOverrides={roundOverrides?.[listKey]}
                      rowOverrides={rowOverrides?.[listKey]}
                      onApplyRoundOverride={(roundNumber, override, clearRowKeys, rowLiveDataIds) => applyRoundOverride(listKey, roundNumber, override, clearRowKeys, rowLiveDataIds)}
                      onApplyRowOverride={(rowKey, override, liveData) => applyRowOverride(listKey, rowKey, override, liveData)}
                      filters={filters}
                      currentUserId={currentUserId}
                      onClearFilters={onClearFilters}
                      tournamentVO={tournamentVO}
                      startScoring={startScoring}
                      openLiveGame={openLiveGame}
                      canManageThisSchedule={canManageThisSchedule}
                      updateMatchSchedule={updateMatchSchedule}
                      deleteMatchRow={deleteMatchRow}
                      assignMatchParticipants={assignMatchParticipants}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {firstRoundCourts.map((court) => (
            <CourtCard key={court.court} court={court} theme={theme} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LadderView;
